/* eslint-disable no-nested-ternary */
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { useField } from 'formik';
import jsCookie from 'js-cookie';

// Components
import SubmitButton from '@atoms/SubmitButton';
import DragDropFile from '@molecules/DragDropFile';
import ImageEditor from '@molecules/ImageEditor';
import { ErrorText } from '@styled';

// Functionality
import useImageCropper from '@hooks/useImageCropper';
import useSaveImageS3 from '@hooks/useSaveImageS3';
import { GlobalModalContext } from '@context/globalModalContext';
import useViewer from '@hooks/useViewer';

const EditProfilePicture = () => {
    const [, setIsModalOpen] = useContext(GlobalModalContext);
    const [photoField] = useField('photoUrl');
    const { updatePhotoUrlLocalCache } = useViewer();
    const [isSuccessCropped, setIsSuccessCropped] = useState(false);
    const [isErrorSaveCropped, setIsErrorSaveCropped] = useState(false);
    const [isNoChange, setIsNoChange] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    // e.g. {height: 3024, width: 3024, x: 0, y: 504}
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const { getCropped } = useImageCropper();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { loadingSignedUrls, loadingFull, saveImageFull, errorFull, saveImageCropped, errorCropped } = useSaveImageS3(
        {
            onSuccessCropped: () => {
                setIsSuccessCropped(true);
            },
            onErrorCropped: () => {
                setIsErrorSaveCropped(true);
                setTimeout(() => {
                    setIsErrorSaveCropped(false);
                }, 3000);
            },
        }
    );

    useEffect(() => {
        // timestamp defeat cache
        setImageSrc(`${photoField.value}?v=${Date.now()}`);
    }, [photoField.value]);

    const onDrop = async acceptedFiles => {
        if (acceptedFiles.length === 0) return;
        try {
            const fullImage = await saveImageFull(acceptedFiles[0]);
            setImageSrc(`${fullImage}?v=${Date.now()}`);
        } catch (e) {
            // console.error(e);
        }
    };

    const updateTopMenuAvatar = imgUrl => {
        const topMenu = document.getElementsByClassName('topMenu')[0];
        const avatar = topMenu?.getElementsByClassName('avatar')[0];
        if (!avatar) return;

        const link = topMenu.getElementsByClassName('login-link')[0];
        const newAvatar = document.createElement('img');
        newAvatar.setAttribute('alt', '');
        newAvatar.setAttribute('src', `${imgUrl}?v=${Date.now()}`);
        newAvatar.classList.add('avatar');
        newAvatar.classList.add('mr-3');
        link.replaceChild(newAvatar, avatar);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            // Originally removed timestamp to avoid CORS cropping issue when
            // image was uploaded, cropped and then recropped.
            // As the saved cropped image is too small now to recrop (200px),
            // the CORS issue isn't possible because zoom is disabled on recrop.
            // So removed this due to the image caching and cropping prev image.
            // But keeping for future refence if it pops up again
            // const urlWithoutParams = imageSrc.split('?')[0];
            const blob = await getCropped({
                file: imageSrc,
                croppedAreaPixels,
                finalHeight: 200,
                finalWidth: 200,
            });

            if (typeof blob === 'undefined') {
                setIsErrorSaveCropped(true);
                setIsNoChange(true);
                setTimeout(() => {
                    setIsErrorSaveCropped(false);
                    setIsNoChange(null);
                }, 3000);
                setIsSubmitting(false);
                throw new Error('Blob is undefined meaning no image was added.');
            }

            const croppedFile = new File([blob], `file-${Date.now()}.jpeg`, {
                type: 'image/jpeg',
                lastModified: new Date(),
            });
            const savedCropped = await saveImageCropped(croppedFile);
            updateTopMenuAvatar(savedCropped);
            updatePhotoUrlLocalCache(savedCropped);
            // to trigger MS refresh in EE template to get new avatar
            jsCookie.set('my_icsc_updated', 1);

            setTimeout(() => {
                setIsModalOpen(false);
            }, 1000);
        } catch (e) {
            // console.error(e);
        }
        setIsSubmitting(false);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixelsOnComplete) => {
        setCroppedAreaPixels(croppedAreaPixelsOnComplete);
    }, []);

    return (
        <>
            <div className="row flex-column-reverse flex-md-row">
                <div className="col-md-6">
                    <ImageEditor imageSrc={imageSrc} onCropComplete={onCropComplete} />
                </div>
                <div className="col-md-6 mb-4 mb-md-0">
                    <DragDropFile onDrop={onDrop} isLoading={loadingFull || loadingSignedUrls} />
                </div>
            </div>
            <div className="row">
                <div className="col-12">
                    {errorFull && (
                        <div className="mt-3">
                            <ErrorText>There was an error uploading your image. Try another file.</ErrorText>
                        </div>
                    )}
                    {errorCropped && (
                        <div className="mt-3">
                            <ErrorText>There was an error cropping your image. Try another file.</ErrorText>
                        </div>
                    )}
                    {isNoChange && (
                        <div className="mt-3">
                            <ErrorText>No changes have been made. Please add a new image prior to saving.</ErrorText>
                        </div>
                    )}

                    <SubmitButton
                        onClick={handleSubmit}
                        isSubmitting={isSubmitting}
                        isError={isErrorSaveCropped}
                        isSuccessful={isSuccessCropped}
                    >
                        Save Profile Image
                    </SubmitButton>
                </div>
            </div>
        </>
    );
};

EditProfilePicture.propTypes = {};

export default EditProfilePicture;
